import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { BounceCategoryChart } from '@/components/bounces/bounce-category-chart'
import { IspTable } from '@/components/bounces/isp-table'
import { RecentBouncesTable } from '@/components/bounces/recent-bounces-table'
import { LoadingPage } from '@/components/ui/loading'
import { MetricCard } from '@/components/ui/metric-card'
import { useBounceAnalytics, useIspStats } from '@/hooks/use-bounces'
import { formatPercent } from '@/lib/format'

type TimeRange = '24h' | '7d' | '30d'

const RANGE_OFFSETS: Record<TimeRange, number> = {
  '24h': 1,
  '7d': 7,
  '30d': 30,
} as const

function rangeToISO(range: TimeRange): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - RANGE_OFFSETS[range])
  return { from: from.toISOString(), to: to.toISOString() }
}

export function BounceAnalyticsPage(): React.JSX.Element {
  const [range, setRange] = useState<TimeRange>('24h')
  const params = rangeToISO(range)

  const bounceQuery = useBounceAnalytics(params)
  const ispQuery = useIspStats(params)

  if (bounceQuery.isLoading) {
    return <LoadingPage message="Loading bounce analytics..." />
  }

  const bounceData = bounceQuery.data

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-6 w-6 text-admin-500" />
          <h1 className="text-2xl font-semibold text-text-primary">Bounce Analytics</h1>
        </div>
        <TimeRangeToggle value={range} onChange={setRange} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard
          label="Hard Bounce Rate"
          value={bounceData ? formatPercent(bounceData.hard_bounce_rate) : '--'}
          variant={bounceData && bounceData.hard_bounce_rate > 5 ? 'danger' : 'default'}
        />
        <MetricCard
          label="Soft Bounce Rate"
          value={bounceData ? formatPercent(bounceData.soft_bounce_rate) : '--'}
          variant={bounceData && bounceData.soft_bounce_rate > 10 ? 'warning' : 'default'}
        />
        <MetricCard
          label="Complaint Rate"
          value={bounceData ? formatPercent(bounceData.complaint_rate) : '--'}
          variant={bounceData && bounceData.complaint_rate > 0.1 ? 'danger' : 'default'}
        />
      </div>

      <BounceCategoryChart categories={bounceData?.categories ?? []} />

      <IspTable data={ispQuery.data?.data ?? []} isLoading={ispQuery.isLoading} />

      <RecentBouncesTable bounces={bounceData?.recent_bounces ?? []} />
    </div>
  )
}

interface TimeRangeToggleProps {
  value: TimeRange
  onChange: (range: TimeRange) => void
}

const TIME_RANGES: TimeRange[] = ['24h', '7d', '30d']

function TimeRangeToggle({ value, onChange }: TimeRangeToggleProps): React.JSX.Element {
  return (
    <div className="inline-flex rounded-lg border border-surface-3 bg-surface-1 p-0.5">
      {TIME_RANGES.map((range) => (
        <button
          type="button"
          key={range}
          onClick={() => onChange(range)}
          className={
            value === range
              ? 'rounded-md bg-admin-500 px-3 py-1.5 text-sm font-medium text-white'
              : 'rounded-md px-3 py-1.5 text-sm font-medium text-text-secondary hover:text-text-primary'
          }
        >
          {range}
        </button>
      ))}
    </div>
  )
}
